import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { type NetworthPeriod, useNetworthHistory } from "../../hooks/networth";
import { formatEur } from "../../lib/format-kpi";
import { type HistoryRow, findYoyMatch } from "../../lib/networth-utils";
import type { ClassKey } from "../networth/SparklinePanel";

const PERIODS: NetworthPeriod[] = ["1M", "3M", "6M", "YTD", "1J", "3J", "5J", "all"];

const PERIOD_LABEL: Record<NetworthPeriod, string> = {
  "1M": "1M",
  "3M": "3M",
  "6M": "6M",
  YTD: "YTD",
  "1J": "1J",
  "3J": "3J",
  "5J": "5J",
  all: "Max",
};

const CLASS_LABELS: Record<ClassKey, string> = {
  LIQUIDE: "Liquide",
  ANLAGEN: "Anlagen",
  IMMOBILIE: "Immobilie",
  SONSTIGE: "Sonstiges",
  VERBINDLICHKEITEN: "Verbindlichkeiten",
};

function formatStichtagShort(s: string): string {
  // YYYY-MM-DD → DD.MM
  const parts = s.split("-");
  return `${parts[2]}.${parts[1]}`;
}

interface TooltipExtra {
  soloClass: ClassKey | null;
  yoyValue: number | null;
  yoyStichtag: string | null;
}

function NetworthTooltip(props: TooltipProps<number, string> & { extra: TooltipExtra }) {
  const { active, payload, label, extra } = props;
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  // Bei aktivem YoY enthaelt payload auch die Overlay-Linie (ohne classes-Feld).
  // Wir picken gezielt die HistoryRow.
  const row = payload.find(
    (p) => p?.payload && typeof p.payload === "object" && "classes" in (p.payload as object),
  )?.payload as HistoryRow | undefined;
  if (!row) return null;
  const currentValue = extra.soloClass ? row.classes[extra.soloClass] : row.totalNetCents;
  const yoyValue = extra.yoyValue;
  const delta = yoyValue !== null ? currentValue - yoyValue : null;
  const deltaPct =
    yoyValue !== null && yoyValue !== 0 && delta !== null ? (delta / yoyValue) * 100 : null;

  return (
    <div
      className="rounded-lg p-3 shadow-lg text-sm"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    >
      <div className="font-semibold mb-1">{label}</div>
      <div className="flex justify-between gap-4">
        <span>
          {extra.soloClass
            ? CLASS_LABELS[extra.soloClass]
            : t("dashboard.networth_chart.tooltip_total", { defaultValue: "Nettovermögen" })}
        </span>
        <span className="num font-semibold">{formatEur(currentValue)}</span>
      </div>
      {yoyValue !== null && extra.yoyStichtag && (
        <>
          <div className="flex justify-between gap-4 text-xs" style={{ color: "var(--muted)" }}>
            <span>Vorjahr ({formatStichtagShort(extra.yoyStichtag)})</span>
            <span className="num">{formatEur(yoyValue)}</span>
          </div>
          {delta !== null && (
            <div
              className="flex justify-between gap-4 text-xs font-semibold"
              style={{ color: delta >= 0 ? "var(--pos)" : "var(--neg)" }}
            >
              <span>Δ</span>
              <span className="num">
                {delta >= 0 ? "+" : ""}
                {formatEur(delta)}
                {deltaPct !== null && ` (${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)}%)`}
              </span>
            </div>
          )}
        </>
      )}
      {!extra.soloClass && (
        <>
          <hr className="my-1" style={{ borderColor: "var(--border)" }} />
          {(["LIQUIDE", "ANLAGEN", "IMMOBILIE", "SONSTIGE", "VERBINDLICHKEITEN"] as const).map(
            (k) => (
              <div
                key={k}
                className="flex justify-between gap-4 text-xs"
                style={{ color: "var(--muted)" }}
              >
                <span>
                  {t(`dashboard.networth_chart.tooltip_${k.toLowerCase()}`, {
                    defaultValue: CLASS_LABELS[k],
                  })}
                </span>
                <span className="num">{formatEur(row.classes[k])}</span>
              </div>
            ),
          )}
        </>
      )}
    </div>
  );
}

export interface NetworthChartProps {
  /** Wenn gesetzt: Komponente rendert ohne Card-Wrapper + Header. */
  embedded?: boolean;
  /** Initiale Periode (Default: "1J"). Wird ignoriert wenn externalData gesetzt ist. */
  defaultPeriod?: NetworthPeriod;
  /** Hoehe des Charts in px (Default: 300). */
  height?: number;
  /** Solo-Modus: zeige nur diese Klasse statt totalNetCents. */
  soloClass?: ClassKey | null;
  /** Wenn true: zweite Linie mit Vorjahresvergleich. */
  showYoy?: boolean;
  /** Klick auf einen Datenpunkt — uebergibt stichtag. */
  onDatapointClick?: (stichtag: string) => void;
  /** Override fuer die History-Daten. Wenn gesetzt, faellt der Picker weg
   *  und der internal Hook wird nicht genutzt. */
  externalData?: HistoryRow[];
  /** Wenn true: kein Periode-Picker rendern (z.B. wenn Picker im Parent ist). */
  hidePicker?: boolean;
}

export function NetworthChart({
  embedded = false,
  defaultPeriod = "1J",
  height = 300,
  soloClass = null,
  showYoy = false,
  onDatapointClick,
  externalData,
  hidePicker = false,
}: NetworthChartProps = {}) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<NetworthPeriod>(defaultPeriod);
  const query = useNetworthHistory(period);
  const rawData: HistoryRow[] = externalData ?? query.data?.history ?? [];
  // Recharts kann function-dataKeys nicht statisch fuers YAxis-Auto-Scaling scannen —
  // bei aktivem YoY (string-dataKey "yoyValue") wuerde die Y-Achse sonst nur den YoY-Bereich
  // abdecken und die Haupt-Linie an die obere Achsen-Kante geclippt. Wir legen den Wert
  // pro Zeile als String-Feld "mainValue" ab.
  const data = rawData
    .filter((r) => r.totalNetCents !== 0)
    .map((r) => ({
      ...r,
      mainValue: soloClass ? r.classes[soloClass] : r.totalNetCents,
    }));

  // Vorjahres-Overlay-Daten: pro Stichtag ein yoyValue (oder null)
  const yoyData = showYoy
    ? data.map((row) => {
        const match = findYoyMatch(data, row.stichtag, 7);
        return {
          stichtag: row.stichtag,
          yoyValue: match ? (soloClass ? match.classes[soloClass] : match.totalNetCents) : null,
        };
      })
    : null;

  // Recharts beruecksichtigt Override-Line-Data (yoyData) NICHT im YAxis-Auto-Domain.
  // Wir berechnen das Max aus Haupt- + YoY-Werten selbst, damit beide Linien sichtbar bleiben.
  const yMax = (() => {
    let max = 0;
    for (const r of data) {
      if (r.mainValue > max) max = r.mainValue;
    }
    if (yoyData) {
      for (const y of yoyData) {
        if (y.yoyValue !== null && y.yoyValue > max) max = y.yoyValue;
      }
    }
    return Math.ceil((max * 1.05) / 1000) * 1000; // 5 % Headroom, auf 1000 €-Cent gerundet
  })();

  const periodPicker = (
    <div className="flex gap-1 flex-wrap">
      {PERIODS.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => setPeriod(p)}
          className="px-2.5 py-1 text-xs rounded-md font-medium transition-colors"
          style={
            period === p
              ? { background: "var(--text)", color: "var(--bg)" }
              : { color: "var(--muted)", background: "var(--card-elev)" }
          }
        >
          {PERIOD_LABEL[p]}
        </button>
      ))}
    </div>
  );

  const body =
    data.length < 2 ? (
      <p className="text-sm py-12 text-center" style={{ color: "var(--muted)" }}>
        {t("dashboard.networth_chart.empty", {
          defaultValue: "Noch keine Snapshots in dieser Periode.",
        })}
      </p>
    ) : (
      <>
        {period !== "all" && data[0] && !externalData && (
          <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>
            {t("dashboard.networth_chart.history_starts_at", {
              defaultValue: "Beginn der Historie: {{date}}",
              date: formatStichtagShort(data[0].stichtag),
            })}
          </p>
        )}
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="stichtag"
                tickFormatter={formatStichtagShort}
                tick={{ fontSize: 11, fill: "var(--muted)" }}
                stroke="var(--border)"
              />
              <YAxis
                tickFormatter={(v: number) => formatEur(v)}
                tick={{ fontSize: 11, fill: "var(--muted)" }}
                stroke="var(--border)"
                width={90}
                domain={[0, yMax]}
              />
              <Tooltip
                content={(props: TooltipProps<number, string>) => {
                  const row = props.payload?.find(
                    (p) =>
                      p?.payload &&
                      typeof p.payload === "object" &&
                      "classes" in (p.payload as object),
                  )?.payload as HistoryRow | undefined;
                  const yoyRow = row ? yoyData?.find((y) => y.stichtag === row.stichtag) : null;
                  return (
                    <NetworthTooltip
                      {...props}
                      extra={{
                        soloClass,
                        yoyValue: yoyRow?.yoyValue ?? null,
                        yoyStichtag:
                          yoyRow && yoyRow.yoyValue !== null && row
                            ? new Date(
                                new Date(`${row.stichtag}T00:00:00.000Z`).getTime() -
                                  365 * 24 * 60 * 60 * 1000,
                              )
                                .toISOString()
                                .slice(0, 10)
                            : null,
                      }}
                    />
                  );
                }}
              />
              {yoyData && (
                <Line
                  type="monotone"
                  data={yoyData}
                  dataKey="yoyValue"
                  stroke="var(--accent)"
                  strokeOpacity={0.35}
                  strokeDasharray="5 3"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              )}
              <Line
                type="monotone"
                dataKey="mainValue"
                stroke="var(--accent)"
                strokeWidth={2}
                dot={{ r: 0 }}
                activeDot={
                  onDatapointClick
                    ? {
                        r: 5,
                        style: { cursor: "pointer" },
                        onClick: (_e: unknown, payload: unknown) => {
                          const row = (payload as { payload?: HistoryRow } | undefined)?.payload;
                          if (row) onDatapointClick(row.stichtag);
                        },
                      }
                    : { r: 0 }
                }
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </>
    );

  if (embedded) {
    return (
      <div className="space-y-3">
        {!externalData && !hidePicker && (
          <div className="flex items-center justify-end">{periodPicker}</div>
        )}
        {body}
      </div>
    );
  }

  return (
    <div className="fv-card p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-lg font-semibold">
          {t("dashboard.networth_chart.title", { defaultValue: "Vermögensverlauf" })}
        </h2>
        {periodPicker}
      </div>
      {body}
    </div>
  );
}
